import { useState } from "react";
import {
  Card, Button, Notice, TextField, Badge, Icon,
} from "@conuti-das/prince-ui";
import { Flex, Spinner, princeIcon } from "@/ui-prince";
import { AutorunCfg, createAutorun, updateAutorun, deleteAutorun, fireAutorun } from "@/api/autoruns";
import { llmApi, LlmApiError } from "@/lib/llmApi";

const TEMPLATE: AutorunCfg = {
  id: "new-autorun", enabled: true, schedule: "0 * * * *",
  probe: { stage: "scrape", mode: "dry-run", scope: { release: "latest" } },
  on_change: {
    create_run: true, triggered_by: "autorun:new-autorun",
    run_stages: ["scrape", "mirror", "parse"]
  },
};

const PROMPT_EXAMPLES = [
  "Jede Nacht um 3 Uhr nachsehen ob neue UTILMD-Releases auf edi-energy.de sind, und wenn ja Tickets erzeugen",
  "Alle 15 Minuten den Mirror aktualisieren, aber nichts deployen",
  "Montags 6 Uhr alle BPMNs für FV 202610 neu bauen und committen",
];

export function AutorunEditor({ initial, onSaved }: {
  initial: AutorunCfg | null; onSaved: () => void;
}) {
  const isNew = !initial;
  const [cfg] = useState<AutorunCfg>(initial ?? TEMPLATE);
  const [yaml, setYaml] = useState<string>(JSON.stringify(initial ?? TEMPLATE, null, 2));
  const [err, setErr] = useState<string | null>(null);

  // ── KI-Agent-Eingabe (nur bei "neu") ─────────────────────────────────────
  const [prompt, setPrompt] = useState<string>("");
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftWarnings, setDraftWarnings] = useState<string[]>([]);

  async function draftFromPrompt() {
    if (!prompt.trim() || drafting) return;
    setDrafting(true);
    setDraftError(null);
    setDraftWarnings([]);
    try {
      const res = await llmApi.draftAutorun(prompt);
      setYaml(JSON.stringify(res.config, null, 2));
      setDraftWarnings(res.warnings);
    } catch (exc) {
      const msg = exc instanceof LlmApiError ? `${exc.detail} (HTTP ${exc.status})` : String(exc);
      setDraftError(msg);
    } finally {
      setDrafting(false);
    }
  }

  async function save() {
    setErr(null);
    try {
      const parsed = JSON.parse(yaml) as AutorunCfg;
      if (isNew) await createAutorun(parsed);
      else await updateAutorun(parsed.id, parsed);
      onSaved();
    } catch (e: any) { setErr(e.message); }
  }
  async function remove() {
    if (!confirm(`Autorun ${cfg.id} wirklich löschen?`)) return;
    await deleteAutorun(cfg.id); onSaved();
  }
  async function fire() {
    await fireAutorun(cfg.id);
    alert(`Autorun ${cfg.id} feuert jetzt.`);
  }

  return (
    <Card padding="none">
      <div style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
        <h3 style={{ margin: 0 }}>{isNew ? "Neuer Autorun" : `Autorun: ${cfg.id}`}</h3>
        {err && <Notice tone="negative">{err}</Notice>}

        {/* KI-Agent: beschreib was der Autorun tun soll, das Modell baut die Config.
            Nur einblenden bei "Neuer Autorun" -- bei Edit ist die Config schon da
            und der Reviewer will sie direkt anpassen, nicht neu generieren. */}
        {isNew && (
          <Card padding="none" style={{ background: "var(--sapInfobar_NonInteractive_Background)" }}>
            <div style={{ padding: "var(--s-3)", display: "flex", flexDirection: "column", gap: "var(--s-2)" }}>
              <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
                <Badge tone="teal">KI-Agent</Badge>
                <span style={{ flex: 1 }}>Beschreib was der Autorun tun soll — der Agent baut die Config.</span>
              </Flex>
              <Flex style={{ gap: "var(--s-2)", alignItems: "stretch" }}>
                <TextField
                  value={prompt}
                  onChange={setPrompt}
                  placeholder='z.B. "Jede Nacht um 3 Uhr neue UTILMD-Releases ziehen und Tickets erzeugen"'
                  className="prn-fill"
                  style={{ flex: 1 }}
                  isDisabled={drafting}
                  onKeyDown={(e) => { if (e.key === "Enter") draftFromPrompt(); }}
                />
                <Button
                  variant="filled"
                  isDisabled={!prompt.trim() || drafting}
                  onPress={draftFromPrompt}
                >
                  <Icon name={princeIcon("learning-assistant")} size={16} />
                  {drafting ? "denkt nach..." : "Config bauen"}
                </Button>
              </Flex>
              {drafting && <Spinner active size="s" />}
              {draftError && <Notice tone="negative">{draftError}</Notice>}
              {draftWarnings.length > 0 && (
                <Notice tone="critical">
                  <strong>Hinweise:</strong>
                  <ul style={{ margin: "4px 0 0 16px" }}>
                    {draftWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </Notice>
              )}
              {/* Vorschlagsleiste -- ein Klick fuellt den Prompt-Input.
                  Hilft Erstnutzern die Syntax zu kapieren. */}
              <Flex style={{ flexWrap: "wrap", gap: 4 }}>
                {PROMPT_EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => setPrompt(ex)}
                    style={{ cursor: "pointer", maxWidth: 280, background: "none", border: "none", padding: 0 }}
                  >
                    <Badge tone="blue">
                      {ex.length > 60 ? ex.slice(0, 60) + "…" : ex}
                    </Badge>
                  </button>
                ))}
              </Flex>
            </div>
          </Card>
        )}

        <label className="prn-field-label">Konfiguration (JSON — wird als YAML gespeichert)</label>
        <textarea className="prn-input" rows={18} value={yaml}
          onChange={(e) => setYaml(e.target.value)}
          style={{ fontFamily: "monospace", fontSize: "0.82rem" }} />
        <Flex style={{ justifyContent: "flex-end", gap: "var(--s-2)" }}>
          {!isNew && <Button variant="filled" onPress={remove}><Icon name={princeIcon("delete")} size={16} />Löschen</Button>}
          {!isNew && <Button variant="tinted" onPress={fire}><Icon name={princeIcon("play")} size={16} />Jetzt feuern</Button>}
          <Button variant="filled" onPress={save}><Icon name={princeIcon("save")} size={16} />Speichern</Button>
        </Flex>
      </div>
    </Card>
  );
}
